import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-google-charts";
import CurrencyFormat from "react-currency-format";

import PageLayout from "../../components/PageLayout";
import ActionLoader from "../../components/ActionLoader";
import Toast from "../../components/Toast";
import useIsMobile from "../../hooks/useIsMobile";

import {
  GET_HEDGE_INTELLIGENCE_URL,
  GET_ENTITIES_URL,
  GET_USERS_URL,
  getData,
} from "../../utils/api";
import { canAccessHedgePlanning, getLoginDetails } from "../../utils/storage";

import InsightsHeaderIcon from "../../icons/newicons/insights.svg";

import "../Converters/converters.css";
import "./insights.css";
import { InsightsSubNav } from "./InsightsSubNav";

const CHART_COLORS = ["#c82534", "#00965a", "#2c6fbb"];

function formatOz(num) {
  if (num == null || Number.isNaN(Number(num))) return "0.00";
  return parseFloat(num).toFixed(2);
}

function formatDateLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getDefaultFromDate() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split("T")[0];
}

function getDefaultToDate() {
  return new Date().toISOString().split("T")[0];
}

function applyDatePreset(days) {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - days);
  return {
    fromDate: from.toISOString().split("T")[0],
    toDate: today.toISOString().split("T")[0],
  };
}

function applyYtdPreset() {
  const today = new Date();
  const from = new Date(today.getFullYear(), 0, 1);
  return {
    fromDate: from.toISOString().split("T")[0],
    toDate: today.toISOString().split("T")[0],
  };
}

function CoverageGauge({ metal, label, coveragePct, gap, gapValueUsd, status }) {
  const pct = Math.min(100, Math.max(0, coveragePct || 0));
  const statusClass =
    status === "at-risk" ? "hedge-planning-status--risk" : status === "over-hedged" ? "hedge-planning-status--over" : "hedge-planning-status--ok";

  return (
    <div className="hedge-planning-gauge-card">
      <div className="hedge-planning-gauge-header">
        <span className="hedge-planning-gauge-label">{label}</span>
        <span className={`hedge-planning-status ${statusClass}`}>{status.replace("-", " ")}</span>
      </div>
      <div className="hedge-planning-gauge-bar-wrap">
        <div className="hedge-planning-gauge-bar" style={{ width: `${pct}%` }} />
      </div>
      <div className="hedge-planning-gauge-meta">
        <span>{formatOz(coveragePct)}% covered</span>
        <span>Gap: {formatOz(gap)} oz</span>
        <span>
          <CurrencyFormat value={gapValueUsd || 0} displayType="text" thousandSeparator prefix="$" decimalScale={0} />
          {" "}at risk
        </span>
      </div>
    </div>
  );
}

function CumulativeChart({ timeSeries, metal }) {
  const key = `cumulative${metal.charAt(0).toUpperCase()}${metal.slice(1)}`;
  const targetKey = `target${metal.charAt(0).toUpperCase()}${metal.slice(1)}`;
  const items = timeSeries || [];
  if (!items.length) return null;

  const data = [
    ["Week", "Acquired", "Hedge target"],
    ...items.map((row) => {
      const weekLabel = new Date(row.weekStart).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return [weekLabel, row[key] || 0, row[targetKey] || 0];
    }),
  ];

  return (
    <div className="hedge-planning-chart-card">
      <div className="hedge-planning-chart-title">Cumulative {metal.toUpperCase()} vs hedge</div>
      <Chart
        chartType="LineChart"
        width="100%"
        height="280px"
        data={data}
        options={{
          legend: { position: "bottom" },
          chartArea: { width: "82%", height: "68%" },
          colors: CHART_COLORS,
          hAxis: { textStyle: { fontSize: 11 } },
          vAxis: { minValue: 0, textStyle: { fontSize: 11 } },
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

function TypeMixChart({ typeMix }) {
  if (!typeMix) return null;
  const types = [
    { label: "Ceramic", pt: typeMix.ceramicPt, pd: typeMix.ceramicPd, rh: typeMix.ceramicRh },
    { label: "Diesel", pt: typeMix.dieselPt, pd: typeMix.dieselPd, rh: typeMix.dieselRh },
    { label: "Foil", pt: typeMix.foilPt, pd: typeMix.foilPd, rh: typeMix.foilRh },
    { label: "After Mkt", pt: typeMix.aftermktPt, pd: typeMix.aftermktPd, rh: typeMix.aftermktrh },
  ];
  const hasData = types.some((t) => (t.pt || 0) + (t.pd || 0) + (t.rh || 0) > 0);
  if (!hasData) return null;

  const data = [
    ["Type", "Pt", "Pd", "Rh"],
    ...types.map((t) => [t.label, t.pt || 0, t.pd || 0, t.rh || 0]),
  ];

  return (
    <div className="hedge-planning-chart-card">
      <div className="hedge-planning-chart-title">Acquired ounces by converter type</div>
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="280px"
        data={data}
        options={{
          legend: { position: "bottom" },
          chartArea: { width: "82%", height: "68%" },
          colors: CHART_COLORS,
          isStacked: false,
          hAxis: { textStyle: { fontSize: 11 } },
          vAxis: { minValue: 0, textStyle: { fontSize: 11 } },
        }}
        loader={<div className="c-gray fs-13">Loading chart...</div>}
      />
    </div>
  );
}

const HedgePlanning = () => {
  const loginDetails = getLoginDetails();
  const canView = canAccessHedgePlanning(loginDetails);
  const isMobile = useIsMobile();

  const [dateRange, setDateRange] = useState({
    fromDate: getDefaultFromDate(),
    toDate: getDefaultToDate(),
  });
  const [mode, setMode] = useState("period");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerItems, setCustomerItems] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyerSearch, setBuyerSearch] = useState("");
  const [buyerItems, setBuyerItems] = useState([]);
  const [showBuyerDropdown, setShowBuyerDropdown] = useState(false);
  const [intel, setIntel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  const getCustomers = async (text) => {
    try {
      const data = await getData(GET_ENTITIES_URL, {
        count: 500,
        offset: 0,
        text: text || "",
        parentCompanyId: loginDetails.parentCompanyId,
        isEmbed: false,
      });
      setCustomerItems(data.list || []);
    } catch (e) {
      console.log("ERROR fetching customers", e);
    }
  };

  const getBuyers = async (text) => {
    try {
      const data = await getData(GET_USERS_URL, {
        count: 500,
        offset: 0,
        text: text || "",
        companyId: loginDetails.companyId,
        isBuyer: true,
      });
      setBuyerItems(data.list || []);
    } catch (e) {
      console.log("ERROR fetching buyers", e);
    }
  };

  const fetchIntelligence = useCallback(async () => {
    if (!canView) return;
    try {
      setIsLoading(true);
      const data = await getData(GET_HEDGE_INTELLIGENCE_URL, {
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        customerId: selectedCustomer?.companyId || "",
        buyerId: selectedBuyer?.userId || "",
        mode,
      });
      setIntel(data);
    } catch (e) {
      console.log("ERROR fetching hedge intelligence", e);
      setIntel(null);
      setToastParams({
        message: "Failed to load hedge planning data.",
        type: "error",
        id: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [canView, dateRange, selectedCustomer, selectedBuyer, mode]);

  useEffect(() => {
    if (!canView) {
      window.location.href = "/";
      return;
    }
    fetchIntelligence();
  }, [canView, fetchIntelligence]);

  useEffect(() => {
    if (customerSearch.length > 2) {
      getCustomers(customerSearch);
      setShowCustomerDropdown(true);
    } else {
      setCustomerItems([]);
      setShowCustomerDropdown(false);
    }
  }, [customerSearch]);

  useEffect(() => {
    if (buyerSearch.length > 2) {
      getBuyers(buyerSearch);
      setShowBuyerDropdown(true);
    } else {
      setBuyerItems([]);
      setShowBuyerDropdown(false);
    }
  }, [buyerSearch]);

  if (!canView) return null;

  const dashboard = intel?.dashboard || {};
  const hedged = dashboard.totalHedged || { pt: 0, pd: 0, rh: 0 };
  const acquired = dashboard.totalAcquired || { pt: 0, pd: 0, rh: 0 };
  const gap = dashboard.difference || { pt: 0, pd: 0, rh: 0 };
  const coverage = intel?.coverage || [];
  const executive = intel?.executive || {};
  const pace = intel?.pace || {};
  const alerts = intel?.alerts || [];
  const contracts = intel?.oemContracts || [];

  const handleClearFilters = () => {
    setDateRange({
      fromDate: getDefaultFromDate(),
      toDate: getDefaultToDate(),
    });
    setMode("period");
    setSelectedCustomer(null);
    setCustomerSearch("");
    setSelectedBuyer(null);
    setBuyerSearch("");
    setCustomerItems([]);
    setBuyerItems([]);
  };

  const ounceLink = `/insights?from=${dateRange.fromDate}&to=${dateRange.toDate}`;

  return (
    <PageLayout icon={InsightsHeaderIcon} heading="Insights" searchPlaceholder="" hideSearchBar>
      <InsightsSubNav />
      <div className="hedge-planning-page">
        <div className="hedge-planning-header">
          <div>
            <h2 className="hedge-planning-title">Hedge Planning</h2>
            <p className="hedge-planning-subtitle">
              Link OEM hedge commitments to converter ounces acquired — plan purchases to meet your hedges.
            </p>
          </div>
          <div className="hedge-planning-quick-actions">
            <Link to="/hedge" className="hedge-planning-link-btn">Manage contracts</Link>
            <Link to={ounceLink} className="hedge-planning-link-btn hedge-planning-link-btn--secondary">
              View ounce breakdown
            </Link>
          </div>
        </div>

        <div className="hedge-planning-filters-card">
          <form
            className="hedge-planning-filters"
            onSubmit={(e) => {
              e.preventDefault();
              fetchIntelligence();
            }}
          >
            <div className="hedge-planning-filter-top">
              <div className="hedge-planning-segment-block">
                <span className="hedge-planning-field-label">Quick range</span>
                <div className="hedge-planning-segments">
                  <button type="button" className="hedge-planning-segment" onClick={() => setDateRange(applyDatePreset(30))}>
                    30 Days
                  </button>
                  <button type="button" className="hedge-planning-segment" onClick={() => setDateRange(applyDatePreset(90))}>
                    90 Days
                  </button>
                  <button type="button" className="hedge-planning-segment" onClick={() => setDateRange(applyYtdPreset())}>
                    YTD
                  </button>
                </div>
              </div>
              <div className="hedge-planning-segment-block">
                <span className="hedge-planning-field-label">View</span>
                <div className="hedge-planning-segments">
                  <button
                    type="button"
                    className={`hedge-planning-segment${mode === "period" ? " is-active" : ""}`}
                    onClick={() => setMode("period")}
                  >
                    Period
                  </button>
                  <button
                    type="button"
                    className={`hedge-planning-segment${mode === "cumulative" ? " is-active" : ""}`}
                    onClick={() => setMode("cumulative")}
                  >
                    Cumulative
                  </button>
                </div>
              </div>
            </div>

            <div className={`hedge-planning-filter-main${isMobile ? " is-mobile" : ""}`}>
              <div className="hedge-planning-field">
                <label className="hedge-planning-field-label" htmlFor="hp-from">From</label>
                <input
                  id="hp-from"
                  type="date"
                  value={dateRange.fromDate}
                  onChange={(e) => setDateRange({ ...dateRange, fromDate: e.target.value })}
                  className="hedge-planning-input"
                />
              </div>
              <div className="hedge-planning-field">
                <label className="hedge-planning-field-label" htmlFor="hp-to">To</label>
                <input
                  id="hp-to"
                  type="date"
                  value={dateRange.toDate}
                  onChange={(e) => setDateRange({ ...dateRange, toDate: e.target.value })}
                  className="hedge-planning-input"
                />
              </div>
              <div className="hedge-planning-field hedge-planning-field--search">
                <label className="hedge-planning-field-label" htmlFor="hp-customer">Customer</label>
                <div className="hedge-planning-search-wrap">
                  <input
                    id="hp-customer"
                    type="text"
                    placeholder="Optional"
                    value={selectedCustomer ? selectedCustomer.companyName : customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      if (!e.target.value) setSelectedCustomer(null);
                    }}
                    className="hedge-planning-input"
                  />
                  {showCustomerDropdown && customerItems.length > 0 && (
                    <div className="hedge-planning-dropdown">
                      {customerItems.slice(0, 8).map((c) => (
                        <button
                          key={c.companyId}
                          type="button"
                          className="hedge-planning-dropdown-item"
                          onMouseDown={() => {
                            setSelectedCustomer(c);
                            setCustomerSearch("");
                            setShowCustomerDropdown(false);
                          }}
                        >
                          {c.companyName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="hedge-planning-field hedge-planning-field--search">
                <label className="hedge-planning-field-label" htmlFor="hp-buyer">Buyer</label>
                <div className="hedge-planning-search-wrap">
                  <input
                    id="hp-buyer"
                    type="text"
                    placeholder="Optional"
                    value={selectedBuyer ? `${selectedBuyer.firstName} ${selectedBuyer.lastName}` : buyerSearch}
                    onChange={(e) => {
                      setBuyerSearch(e.target.value);
                      if (!e.target.value) setSelectedBuyer(null);
                    }}
                    className="hedge-planning-input"
                  />
                  {showBuyerDropdown && buyerItems.length > 0 && (
                    <div className="hedge-planning-dropdown">
                      {buyerItems.slice(0, 8).map((b) => (
                        <button
                          key={b.userId}
                          type="button"
                          className="hedge-planning-dropdown-item"
                          onMouseDown={() => {
                            setSelectedBuyer(b);
                            setBuyerSearch("");
                            setShowBuyerDropdown(false);
                          }}
                        >
                          {b.firstName} {b.lastName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="hedge-planning-field hedge-planning-field--actions">
                <span className="hedge-planning-field-label hedge-planning-field-label--spacer">&nbsp;</span>
                <div className="hedge-planning-action-btns">
                  <button
                    type="button"
                    className="btn insights-btn-clear ht-40 hedge-planning-clear-btn"
                    onClick={handleClearFilters}
                  >
                    <span className="fw-bolder">Clear</span>
                  </button>
                  <button type="submit" className="btn ht-40 hedge-planning-apply-btn">
                    <span className="fw-bolder">Apply</span>
                  </button>
                </div>
              </div>
            </div>

            <p className="hedge-planning-filter-hint">
              {mode === "cumulative"
                ? "Cumulative view: ounces acquired since earliest active hedge through the selected end date."
                : `Showing period ${formatDateLabel(dateRange.fromDate)} – ${formatDateLabel(dateRange.toDate)}`}
            </p>
          </form>
        </div>

        {isLoading && <ActionLoader />}

        {!isLoading && intel && (
          <>
            {alerts.length > 0 && (
              <div className="hedge-planning-alerts mb-20">
                {alerts.map((a, i) => (
                  <div key={i} className={`hedge-planning-alert hedge-planning-alert--${a.type}`}>
                    {a.message}
                  </div>
                ))}
              </div>
            )}

            <div className="hedge-planning-hero-row mb-20">
              <div className="hedge-planning-hero-card">
                <div className="hedge-planning-hero-label">Total Hedged</div>
                <div className="hedge-planning-hero-metals">
                  <span>Pt {formatOz(hedged.pt)}</span>
                  <span>Pd {formatOz(hedged.pd)}</span>
                  <span>Rh {formatOz(hedged.rh)}</span>
                </div>
              </div>
              <div className="hedge-planning-hero-card hedge-planning-hero-card--acquired">
                <div className="hedge-planning-hero-label">Acquired (converters)</div>
                <div className="hedge-planning-hero-metals">
                  <span>Pt {formatOz(acquired.pt)}</span>
                  <span>Pd {formatOz(acquired.pd)}</span>
                  <span>Rh {formatOz(acquired.rh)}</span>
                </div>
              </div>
              <div className="hedge-planning-hero-card hedge-planning-hero-card--gap">
                <div className="hedge-planning-hero-label">Gap (hedged − acquired)</div>
                <div className="hedge-planning-hero-metals">
                  <span>Pt {formatOz(gap.pt)}</span>
                  <span>Pd {formatOz(gap.pd)}</span>
                  <span>Rh {formatOz(gap.rh)}</span>
                </div>
              </div>
            </div>

            <div className="hedge-planning-executive card p-20 mb-20">
              <div className="hedge-planning-executive-grid">
                <div>
                  <div className="hedge-planning-exec-label">Hedged value</div>
                  <CurrencyFormat
                    className="hedge-planning-exec-value"
                    value={executive.hedgedValueUsd || 0}
                    displayType="text"
                    thousandSeparator
                    prefix="$"
                    decimalScale={0}
                  />
                </div>
                <div>
                  <div className="hedge-planning-exec-label">Acquired value (at hedge prices)</div>
                  <CurrencyFormat
                    className="hedge-planning-exec-value"
                    value={executive.acquiredValueUsd || 0}
                    displayType="text"
                    thousandSeparator
                    prefix="$"
                    decimalScale={0}
                  />
                </div>
                <div>
                  <div className="hedge-planning-exec-label">Open gap value</div>
                  <CurrencyFormat
                    className="hedge-planning-exec-value hedge-planning-exec-value--gap"
                    value={executive.gapValueUsd || 0}
                    displayType="text"
                    thousandSeparator
                    prefix="$"
                    decimalScale={0}
                  />
                </div>
              </div>
            </div>

            <div className="hedge-planning-gauges mb-20">
              {coverage.map((c) => (
                <CoverageGauge
                  key={c.metal}
                  metal={c.metal}
                  label={c.label}
                  coveragePct={c.coveragePct}
                  gap={c.gap}
                  gapValueUsd={c.gapValueUsd}
                  status={c.status}
                />
              ))}
            </div>

            <div className="hedge-planning-pace card p-20 mb-20">
              <h3 className="hedge-planning-section-title">Purchase pace & runway</h3>
              <p className="hedge-planning-pace-hint">
                Based on {pace.daysInRange || 1} day(s) in range
              </p>
              <div className="hedge-planning-pace-grid">
                {["pt", "pd", "rh"].map((metal) => {
                  const p = pace[metal] || {};
                  const daysToClose = p.daysToCloseGap;
                  return (
                    <div key={metal} className="hedge-planning-pace-item">
                      <strong>{metal.toUpperCase()}</strong>
                      <span>Daily rate: {formatOz(p.dailyRate)} oz</span>
                      <span>
                        {daysToClose >= 0
                          ? `At current rate, close gap in ~${Math.ceil(daysToClose)} days`
                          : "Insufficient acquisition rate to project"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="hedge-planning-charts mb-20">
              <CumulativeChart timeSeries={intel.timeSeries} metal="pt" />
              <TypeMixChart typeMix={intel.typeMix} />
            </div>

            <div className="card p-20 mb-20">
              <h3 className="hedge-planning-section-title">OEM contracts (pro-rata gap)</h3>
              <div className="insights-metal-table-wrap">
                <table className="insights-metal-table hedge-planning-table">
                  <thead>
                    <tr>
                      <th>OEM</th>
                      <th>Date</th>
                      <th>Contract value</th>
                      <th>Pt gap</th>
                      <th>Pd gap</th>
                      <th>Rh gap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center c-gray">
                          No active hedge contracts. <Link to="/hedge">Add a contract</Link>
                        </td>
                      </tr>
                    ) : (
                      contracts.map((c) => (
                        <tr key={c.hedgeId}>
                          <td>{c.oemName}</td>
                          <td>{c.hedgeDate}</td>
                          <td>
                            <CurrencyFormat
                              value={c.contractValue || 0}
                              displayType="text"
                              thousandSeparator
                              prefix="$"
                              decimalScale={0}
                            />
                          </td>
                          <td>{formatOz(c.proRataGapPt)}</td>
                          <td>{formatOz(c.proRataGapPd)}</td>
                          <td>{formatOz(c.proRataGapRh)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {toastParams.message && (
        <Toast
          message={toastParams.message}
          type={toastParams.type}
          id={toastParams.id}
          onClose={() => setToastParams({ message: "", type: "", id: 0 })}
        />
      )}
    </PageLayout>
  );
};

export default HedgePlanning;
